import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  claimAutomaticRedirect,
  resetRedirectGuard
} from './login-redirect-guard';
import { loginOptions, withoutInviteParams } from './login-options';
import usePermissionsTokenReady from './usePermissionsTokenReady';
import type { SignInErrorText } from '../models/auth0/sign-in-error-text.interface';

// The portal's login startup, the same flow as the TMS shell's useAuthStartup.

// How long the portal waits for auth0-react's startup silent-authentication check before it
// stops waiting and falls back to an interactive login. A silent-auth request that is rejected
// without a postMessage only ends when the SDK's 60 s iframe timeout fires; that SDK option is
// global and also bounds every silent token renewal, so the portal bounds only its own wait.
export const SILENT_AUTH_STARTUP_LIMIT_MS = 10_000;

// OAuth error codes that mean "the user has to interact with the login page". An interactive
// login resolves them, so the portal redirects. Any other error (access_denied from a post-login
// Action, a failed token exchange, an invalid state, a network or origin failure) would fail
// the same way again, so the portal shows it instead of redirecting into a loop.
const INTERACTIVE_LOGIN_ERRORS = [
  'login_required',
  'consent_required',
  'interaction_required'
];

// auth0-react surfaces OAuth failures as GenericError (@auth0/auth0-spa-js) or OAuthError,
// both of which carry the OAuth error code in `error`. Plain Errors carry none.
const oauthErrorCode = (error: Error): string | undefined => {
  const code = (error as { error?: unknown }).error;
  return typeof code === 'string' ? code : undefined;
};

const requiresInteractiveLogin = (error: Error) =>
  INTERACTIVE_LOGIN_ERRORS.includes(oauthErrorCode(error) ?? '');

// A post-login Action denied this user (for example an unverified email). Logging in again would
// be denied the same way, possibly without the login page if Auth0 kept a session, so the portal
// offers only a logout back to the login page.
const ACCESS_DENIED = 'access_denied';

// Our own text (a locale key) for each error the sign-in service is known to send back. The
// error's message comes from error_description in the callback URL, which anyone can write into
// a link, so it is never the main message; the error screen shows it only on a secondary line
// marked as coming from the sign-in service.
// A Map, not an object literal: the code comes from the URL, and ?error=constructor must not
// find Object.prototype's.
const SIGN_IN_ERROR_TEXT = new Map<string, SignInErrorText>([
  [ACCESS_DENIED, 'SignInAccessDenied'],
  // A blocked user, or an application not enabled for the user's connection.
  ['unauthorized', 'SignInUnauthorized'],
  // auth0-spa-js: the login transaction is gone or does not match (an old or reused link).
  ['missing_transaction', 'SignInExpired'],
  ['state_mismatch', 'SignInExpired'],
  ['server_error', 'SignInServiceUnavailable'],
  ['temporarily_unavailable', 'SignInServiceUnavailable'],
  ['timeout', 'SignInServiceUnavailable']
]);

const signInErrorText = (code: string | undefined): SignInErrorText =>
  SIGN_IN_ERROR_TEXT.get(code ?? '') ?? 'SignInUnexpectedError';

// auth0-spa-js uses the code as the message when the callback has no error_description;
// repeating it adds nothing.
const serviceMessageOf = (error: Error, code: string | undefined) =>
  error.message && error.message !== code ? error.message : undefined;

export type AuthStartupStatus =
  | 'loading'
  | 'authenticated'
  | 'error'
  | 'accessDenied'
  | 'redirectLoop';

export interface AuthStartup {
  status: AuthStartupStatus;
  // Set with 'error' and 'accessDenied'.
  errorText?: SignInErrorText;
  serviceMessage?: string;
  // The key to mount the layout under; it changes when a token with permissions arrives late.
  layoutKey: number;
  retry: () => void;
  backToLogin: () => void;
}

const useAuthStartup = (): AuthStartup => {
  const { isLoading, user, error, loginWithRedirect, logout } = useAuth0();
  // The first login's token has no permissions yet; the layout calls APIs as soon as it mounts.
  const { tokenReady, layoutKey } = usePermissionsTokenReady();
  const [silentAuthTimedOut, setSilentAuthTimedOut] = useState(false);
  const [redirectBlocked, setRedirectBlocked] = useState(false);
  const redirectRequested = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }
    const timer = setTimeout(
      () => setSilentAuthTimedOut(true),
      SILENT_AUTH_STARTUP_LIMIT_MS
    );
    return () => clearTimeout(timer);
  }, [isLoading]);

  const blockingError =
    error && !requiresInteractiveLogin(error) ? error : undefined;
  const needsLogin =
    (!isLoading || silentAuthTimedOut) && !user && !blockingError;

  useEffect(() => {
    if (user) {
      resetRedirectGuard();
      return;
    }
    if (!needsLogin || redirectRequested.current) {
      return;
    }
    redirectRequested.current = true;
    if (claimAutomaticRedirect()) {
      void loginWithRedirect(loginOptions());
    } else {
      setRedirectBlocked(true);
    }
  }, [user, needsLogin, loginWithRedirect]);

  // Signed in: take the invite params out of the address through the router, so its location
  // (useSearchParams included) no longer has them either, keeping the location state.
  useEffect(() => {
    if (!user) {
      return;
    }
    const withoutInvite = withoutInviteParams(location);
    if (withoutInvite) {
      navigate(withoutInvite, { replace: true, state: location.state });
    }
  }, [user, location, navigate]);

  const retry = () => {
    resetRedirectGuard();
    void loginWithRedirect(loginOptions());
  };

  const backToLogin = () => {
    resetRedirectGuard();
    void logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (blockingError) {
    const code = oauthErrorCode(blockingError);
    return {
      status: code === ACCESS_DENIED ? 'accessDenied' : 'error',
      errorText: signInErrorText(code),
      serviceMessage: serviceMessageOf(blockingError, code),
      layoutKey,
      retry,
      backToLogin
    };
  }
  if (redirectBlocked) {
    return { status: 'redirectLoop', layoutKey, retry, backToLogin };
  }
  return {
    status: user && tokenReady ? 'authenticated' : 'loading',
    layoutKey,
    retry,
    backToLogin
  };
};

export default useAuthStartup;
